import { type Editor } from '@tiptap/core'
import { getAiToolkit } from '@tiptap-pro/ai-toolkit'
import classNames from 'classnames'
import { useEffect, useRef, useState, type VFC } from 'react'
import { Button, Icon } from '@opallabs/lapidary'
import { recordSuggestionDecisions } from '../../../ai-review/suggestionReview'
import * as styles from './styles'

type AIReviewBannerProps = {
  editor: Editor | null
  documentId?: string
  className?: string
}

/**
 * Reactively read the number of AI suggestions awaiting review, and whether
 * the current batch has been partially reviewed (the count has dropped below
 * its high-water mark) — the buttons then say "Remaining" instead of "All" so
 * finishing a partial review doesn't read as reverting the accepted edits.
 */
const useSuggestionReview = (
  editor: Editor | null
): { count: number; partiallyReviewed: boolean } => {
  const [count, setCount] = useState(0)
  const maxSeenRef = useRef(0)

  useEffect(() => {
    if (!editor) {
      maxSeenRef.current = 0
      setCount(0)
      return
    }

    const getSuggestions = () => {
      const next = getAiToolkit(editor).getSuggestions().length
      // A drained batch resets the high-water mark so the next batch starts
      // back at "All".
      maxSeenRef.current = next === 0 ? 0 : Math.max(maxSeenRef.current, next)
      setCount(next)
    }

    getSuggestions()
    editor.on('transaction', getSuggestions)
    return () => {
      editor.off('transaction', getSuggestions)
    }
  }, [editor])
  return { count, partiallyReviewed: count > 0 && count < maxSeenRef.current }
}

export const AIReviewBanner: VFC<AIReviewBannerProps> = ({
  editor,
  documentId,
  className,
}) => {
  const { count: suggestionCount, partiallyReviewed } =
    useSuggestionReview(editor)

  if (!editor || suggestionCount === 0) return null

  const acceptLabel = partiallyReviewed ? 'Accept Remaining' : 'Accept All'
  const dismissLabel = partiallyReviewed ? 'Dismiss Remaining' : 'Dismiss All'

  const onAccept = () => {
    const toolkit = getAiToolkit(editor)
    const count = toolkit.getSuggestions().length
    toolkit.acceptAllSuggestions()
    recordSuggestionDecisions(editor, documentId, 'accepted', count)
  }

  const onReject = () => {
    const toolkit = getAiToolkit(editor)
    const count = toolkit.getSuggestions().length
    toolkit.rejectAllSuggestions()
    recordSuggestionDecisions(editor, documentId, 'rejected', count)
  }

  return (
    <div
      aria-label="AI suggestions awaiting review"
      className={classNames('text-editor-ai-review-banner', className)}
      css={styles.bannerContainer}
      role="status"
    >
      <div css={styles.bannerContent}>
        <div css={styles.bannerMessage}>
          <Icon glyph="gem-ai" height={20} width={20} ariaHidden />
          <span>
            {`${suggestionCount} Gem ${
              suggestionCount === 1 ? 'edit' : 'edits'
            } awaiting review`}
          </span>
        </div>
        <div css={styles.bannerActions}>
          <Button
            buttonText={acceptLabel}
            name={acceptLabel}
            onClick={onAccept}
            variant="primaryGem"
          />
          <Button
            buttonText={dismissLabel}
            name={dismissLabel}
            onClick={onReject}
            variant="secondaryGem"
          />
        </div>
      </div>
    </div>
  )
}
